import { datumOmgedraaid, kalenderdag } from './datum.js';
import { collator, filterbaar, klein, label, norm, vindKolom } from './kolom.js';
import type { Context, Datumbereik, Facet, FilterStand, Filterwaarde, Kolom, Lijst, Sortering, Suggesties } from './typen.js';

type Toets<R> = (rij: R) => boolean;

const isBereik = (f: Filterwaarde): f is Datumbereik => typeof f === 'object' && !Array.isArray(f);

/** Predicaat voor één kolomfilter, of `null` als het filter niets doet. */
function toets<R, C extends Context>(k: Kolom<R, C>, f: Filterwaarde | undefined, ctx: C): Toets<R> | null {
  if (f === undefined || !filterbaar(k)) return null;
  if (k.type === 'keuzelijst' && Array.isArray(f)) {
    if (f.length === 0) return null;
    const gekozen = new Set(f);
    return (r) => gekozen.has(norm(k.waarde(r, ctx)));
  }
  if (k.type === 'datum' && isBereik(f)) {
    if (datumOmgedraaid(f)) return null;
    const van = f.van ? kalenderdag(f.van) : null;
    const tot = f.tot ? kalenderdag(f.tot) : null;
    if (van === null && tot === null) return null;
    return (r) => {
      const w = norm(k.waarde(r, ctx));
      const dag = w === null ? null : kalenderdag(w);
      return dag !== null && (van === null || dag >= van) && (tot === null || dag <= tot);
    };
  }
  if (k.type === 'tekst' && typeof f === 'string') {
    const tekst = klein(f.trim());
    if (tekst === '') return null;
    return (r) => {
      const w = norm(k.waarde(r, ctx));
      if (w === null) return false;
      return k.alleenKeuze ? klein(w) === tekst : klein(w).includes(tekst);
    };
  }
  return null;
}

export function pasFiltersToe<R, C extends Context>(rijen: readonly R[], lijst: Lijst<R, C>, filters: FilterStand, ctx: C): R[] {
  const toetsen = lijst.kolommen.map((k) => toets(k, filters[k.sleutel], ctx)).filter((t): t is Toets<R> => t !== null);
  return rijen.filter((r) => toetsen.every((t) => t(r)));
}

function zonder(filters: FilterStand, sleutel: string): FilterStand {
  const { [sleutel]: _weg, ...rest } = filters;
  return rest;
}

/**
 * Keuzes per keuzelijstkolom, trapsgewijs (§4): kolom X telt over de rijen die alle filters behalve X doorlaten.
 * ponytail: één filterronde per facetkolom, O(facetten × rijen × filters); voorberekenen als de productiemeting boven 300 ms komt.
 */
export function facetten<R, C extends Context>(
  rijen: readonly R[],
  lijst: Lijst<R, C>,
  filters: FilterStand,
  sleutels: readonly string[],
  ctx: C,
): Record<string, Facet[]> {
  const uit: Record<string, Facet[]> = {};
  for (const sleutel of sleutels) {
    const k = vindKolom(lijst, sleutel);
    if (!k) continue;
    const tellingen = new Map<string | null, number>();
    for (const r of pasFiltersToe(rijen, lijst, zonder(filters, sleutel), ctx)) {
      const w = norm(k.waarde(r, ctx));
      tellingen.set(w, (tellingen.get(w) ?? 0) + 1);
    }
    const eigen = filters[sleutel];
    const gekozen = new Set(Array.isArray(eigen) ? eigen : []);
    for (const w of gekozen) if (!tellingen.has(w)) tellingen.set(w, 0);
    uit[sleutel] = [...tellingen]
      .map(([waarde, aantal]) => ({ waarde, aantal }))
      .sort((a, b) => {
        const g = Number(gekozen.has(b.waarde)) - Number(gekozen.has(a.waarde));
        if (g !== 0) return g;
        if (a.waarde === null || b.waarde === null) return Number(a.waarde === null) - Number(b.waarde === null);
        return collator.compare(label(k, a.waarde, ctx), label(k, b.waarde, ctx));
      });
  }
  return uit;
}

const MAX_SUGGESTIES = 10;

/** Bestaande waarden van een tekstkolom die de tekst bevatten (op waarde of label), trapsgewijs, meest voorkomend eerst. */
export function suggesties<R, C extends Context>(
  rijen: readonly R[],
  lijst: Lijst<R, C>,
  filters: FilterStand,
  sleutel: string,
  tekst: string,
  ctx: C,
): Suggesties {
  const k = vindKolom(lijst, sleutel);
  const gezocht = klein(tekst.trim());
  if (!k || gezocht.length < (k.minTekens ?? 0)) return { waarden: [], meer: false };
  const tellingen = new Map<string, number>();
  for (const r of pasFiltersToe(rijen, lijst, zonder(filters, sleutel), ctx)) {
    const w = norm(k.waarde(r, ctx));
    if (w === null || !(klein(w).includes(gezocht) || klein(label(k, w, ctx)).includes(gezocht))) continue;
    tellingen.set(w, (tellingen.get(w) ?? 0) + 1);
  }
  const waarden = [...tellingen].sort((a, b) => b[1] - a[1] || collator.compare(a[0], b[0])).map(([w]) => w);
  return { waarden: waarden.slice(0, MAX_SUGGESTIES), meer: waarden.length > MAX_SUGGESTIES };
}

/** Staan filters en sortering op hun standaard? (reset-knop uitgeschakeld; de pagina telt niet.) */
export function isStandaard(filters: FilterStand, sortering: Sortering): boolean {
  if (sortering.length > 0) return false;
  return Object.values(filters).every((f: Filterwaarde | undefined) => {
    if (f === undefined) return true;
    if (Array.isArray(f)) return f.length === 0;
    if (typeof f === 'string') return f.trim() === '';
    return !f.van?.trim() && !f.tot?.trim();
  });
}
